import { LogicNodeDefinition } from '../LogicNodeDefinition';
import type { SimulationTiming } from '../SimulationEventQueue';

interface DelayState {
  delayMs: number;
  queue: Array<{ value: boolean; targetTime: number }>;
  currentOutput: boolean;
  lastProcessTime?: number;
}

// Queue deadlines are serialized with the project. performance.now() resets on
// navigation, leaving restored delay nodes waiting for an old page's clock.
const now = () => Date.now();

// Runtime scheduling stays outside serializable, non-observable node state.
const schedulers = new WeakMap<
  DelayState,
  {
    schedule: () => void;
    timing?: SimulationTiming;
    scheduledTime: () => number | undefined;
  }
>();

export const DelayNode: LogicNodeDefinition<DelayState> = {
  type: 'DELAY',
  configurationKeys: ['delayMs'],
  createDefaultState: () => ({
    delayMs: 1000,
    queue: [],
    currentOutput: false,
  }),
  setup: (state, trigger, timing) => {
    let cancelTimer: (() => void) | undefined;
    let scheduledTime: number | undefined;
    const restored = timing?.restoredScheduler;
    if (restored && restored.kind !== 'delay') throw Error('Invalid Delay continuation');
    const issue = (targetTime: number | undefined) => {
      if (targetTime === scheduledTime) return;
      cancelTimer?.();
      scheduledTime = targetTime;
      cancelTimer = undefined;
      if (targetTime === undefined) return;
      const wake = () => {
        cancelTimer = undefined;
        scheduledTime = undefined;
        trigger();
      };
      if (timing) cancelTimer = timing.schedule(targetTime, wake);
      else {
        const timer = setTimeout(wake, Math.max(0, targetTime - now()));
        cancelTimer = () => clearTimeout(timer);
      }
    };
    const schedule = () => issue(state.queue[0]?.targetTime);
    schedulers.set(state, { schedule, timing, scheduledTime: () => scheduledTime });
    if (restored) issue(restored.scheduledTime ?? undefined);
    else schedule();
    return () => {
      schedulers.delete(state);
      cancelTimer?.();
    };
  },
  needsInputSync: (input, state) =>
    input !==
    (state.queue.length > 0
      ? state.queue[state.queue.length - 1].value
      : state.currentOutput),
  process: (input, state, _deltaTime) => {
    const scheduler = schedulers.get(state);
    const currentTime = scheduler?.timing?.now() ?? now();
    state.lastProcessTime = currentTime;

    const lastValue =
      state.queue.length > 0
        ? state.queue[state.queue.length - 1].value
        : state.currentOutput;

    if (input !== lastValue) {
      state.queue.push({ value: input, targetTime: currentTime + state.delayMs });
    }

    // A busy browser can wake after both edges of a pulse are due. Preserve the
    // queued waveform's spacing, rather than collapsing it to a single value
    // or a one-wave glitch too short for downstream latches to capture.
    if (state.queue.length > 0 && state.queue[0].targetTime <= currentTime) {
      const next = state.queue.shift()!;
      state.currentOutput = next.value;
      const lateness = currentTime - next.targetTime;
      if (lateness > 0) for (const queued of state.queue) queued.targetTime += lateness;
    }

    scheduler?.schedule();
    return state.currentOutput;
  },
};

export function captureDelayScheduler(state: DelayState) {
  const runtime = schedulers.get(state);
  return runtime?.timing?.resumable
    ? {
        kind: 'delay' as const,
        scheduledTime: runtime.scheduledTime() ?? null,
      }
    : null;
}
